import { Form, Tag } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import {
  getDefaultOrganizationTestingEndDate,
  OrganizationItem,
  OrganizationItemStore,
  ORGANIZATIONS_MEMBER_LIMIT,
} from 'src/stores/organizations-store';
import { container } from 'src/inversify.config';
import { Alert, Input, Spin, Tabs, Col, Button, Row, App, Checkbox, Typography } from 'antd';
import { isEmailOrGuid, nameof } from 'src/core/utils/object';
import LanguageSelect from 'src/common/components/business/language-select';
import PartnerInfo from 'src/common/partner-info';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import DefaultRolesSelect from 'src/common/components/business/default-roles-select';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Country, Selector } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import NewItemModal from 'src/core/ui/newitemmodal';
import { RoleWithExpirationDate } from '../../stores/role-assignments-store';
import SelectCountry from 'src/common/components/business/country-select';
import moment from 'moment';
import { getDateFormat } from 'src/common/utils';
import { RoleAssignmentAttributes } from 'src/stores/role-assignments-store';
import RoleAssignmentAttributesComponent from '../roles/role-attributes/role-attributes';
import { StructmasterUserRoleName } from 'src/stores/roles-store';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';

const { TextArea } = Input;
const { Text } = Typography;

interface FormData {
  displayName: string;
  email: string;
  address: string;
  defaultLanguage: string;
  ownerUser: { label: string; value: string };
  initialUsers: { user: { label: string; value: string }; roles: { label: string; value: string }[] }[];
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
  ownerRolesExpirationDate: Date;
}

const NewOrganizationDialog: FC<{ onClose: (response?: CommandResult<OrganizationItem>) => void }> = ({ onClose }) => {
  const identity = useIdentity();
  const [form] = Form.useForm<FormData>();
  const { t } = useTranslation();
  const newOrganizationStore = container.get(OrganizationItemStore);
  const newOrganizationState = newOrganizationStore.state;
  const [savedAndError, setSavedAndError] = useState(false);
  const [customError, setCustomError] = useState('');
  const partnerInfo = container.get<PartnerInfo>(PartnerInfo);
  const [roleAttributes, setRoleAttributes] = useState<{ [key: number]: RoleAssignmentAttributes }>();

  const currentOrganizationMaster = useRef('');
  const [isTestOrg, setIsTestOrg] = useState(false);
  const { modal } = App.useApp();

  const onNewItem = async (item: FormData) => {
    let response = await newOrganizationStore.create({
      address: curateString(item.address),
      country: item.country,
      defaultLanguage: item.defaultLanguage,
      displayName: curateString(item.displayName),
      email: item.email,
      parentOrganizationId: partnerInfo.organizationId,
      ownerUserIdOrEmail: item.ownerUser.value,
      isTestingOrg: isTestOrg,
      testingEndDate: item.testingEndDate,
      ownerRolesExpirationDate: item.ownerRolesExpirationDate,
      initialUsers: (item.initialUsers || []).map((x, i) => ({
        userIdOrEmail: x.user.value,
        defaultRoleIds: (x.roles || []).map((r) => {
          return roleAttributes && roleAttributes[i].expirationDate
            ? ({ roleId: r.value, role: r.label, expirationDate: roleAttributes[i].expirationDate } as RoleWithExpirationDate)
            : { role: r.label, roleId: r.value, expirationDate: undefined };
        }),
      })),
    });

    if (response?.isSuccess) {
      form.resetFields();
      onClose(response);
      setSavedAndError(true);
    } else {
      setSavedAndError(true);
    }
  };

  useEffect(() => {
    const loadParentOrganization = async () => {
      await newOrganizationStore.load(partnerInfo.organizationId);
      if (newOrganizationState.item.isTestingOrg.value) setIsTestOrg(true);
    };

    loadParentOrganization();
    setSavedAndError(false);
    newOrganizationStore.clearMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onRoleAttributesChange = (item: RoleAssignmentAttributes, fieldIdx: number) => {
    var attr = { ...roleAttributes };
    attr[fieldIdx] = item;
    setRoleAttributes(attr);
  };

  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewOrganization')}
      onNewItem={async () => {
        let item: any;
        try {
          item = await form.validateFields();
        } catch {
          setCustomError(t('CheckOrganizationInfoAndUsersTabs'));
          return;
        }
        setCustomError('');
        if (isTestOrg) {
          modal.confirm({
            title: t('TestOrgConfirmationMessage'),
            onOk: () => onNewItem(item),
          });
        } else onNewItem(item);
      }}
      onClose={() => onClose(savedAndError ? ({} as any) : undefined)}
      onOkButtonIsDisabled={newOrganizationState.isBusy.get()}
      onCancelButtonIsDisabled={newOrganizationState.isBusy.get()}
      closable={!newOrganizationState.isBusy.get()}
      maskClosable={!newOrganizationState.isBusy.get()}
      okText={t('AddOrganization')}
      width={720}
      errorMessage={newOrganizationState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newOrganizationState.isBusy.get()} size="large">
            <Form form={form} layout="vertical" autoComplete="off" initialValues={{ isPartner: false }}>
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('Organization Info')} key="1">
                  <Form.Item
                    name={nameof<FormData>('displayName')}
                    rules={[
                      { required: true, message: t('PleaseEnterName') },
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 128;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('DisplayName')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('DisplayName')} />
                  </Form.Item>
                  <Form.Item
                    name={nameof<FormData>('email')}
                    rules={[
                      { required: true, type: 'email', message: t('PleaseEnterAValidEmail') },
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 256;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Email')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('Email')} />
                  </Form.Item>
                  <Form.Item name={nameof<FormData>('address')} rules={[{ required: true, message: t('PleaseEnterAnAddress') }]}>
                    <TextArea rows={3} autoComplete="off" allowClear placeholder={t('Address')} />
                  </Form.Item>
                  <Form.Item name={nameof<FormData>('country')} rules={[{ required: true, message: t('PleaseSelectCountry') }]}>
                    <SelectCountry placeholder={t('Country')} />
                  </Form.Item>
                  <Form.Item
                    initialValue={identity.profile['default_lang']}
                    name="defaultLanguage"
                    rules={[{ required: true, message: t('PleaseSelectALanguage') }]}
                  >
                    <LanguageSelect />
                  </Form.Item>
                  <Row gutter={8} style={{ width: '100%' }}>
                    <Col xxl={16} xl={16} sm={16} lg={16} xs={24}>
                      <Form.Item
                        name={nameof<FormData>('ownerUser')}
                        rules={[
                          () => ({
                            required: true,
                            validator(_, value) {
                              currentOrganizationMaster.current = value?.value;
                              if (value?.value == null) return Promise.reject(t('PleaseSelectOrInvite'));
                              if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <UserAutocomplete placeholder={t('SelectOrInviteOrganizationMaster')} />
                      </Form.Item>
                    </Col>

                    <Col xxl={8} xl={8} sm={8} lg={8} xs={24}>
                      <RoleAssignmentAttributesComponent
                        customFormFieldName={'ownerRolesExpirationDate'}
                        onChange={() => {}}
                        showLabel={false}
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={0}>
                    {newOrganizationState.item.value && !newOrganizationState.item.isTestingOrg?.value && (
                      <Col sm={12} xs={24}>
                        <Form.Item name={nameof<FormData>('isTestingOrg')} valuePropName="checked" initialValue={false}>
                          <Checkbox
                            onChange={(e) => {
                              setIsTestOrg(e.target.checked);
                              if (!e.target.checked) {
                                form.setFieldValue('testingEndDate', undefined);
                              }
                            }}
                          >
                            {t('IsTestOrganization')}
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    )}

                    {newOrganizationState.item.value && newOrganizationState.item.isTestingOrg?.value && (
                      <Col sm={12} xs={24}>
                        <Tag>{t('TestOrganization')}</Tag>
                      </Col>
                    )}

                    {isTestOrg && newOrganizationState.item.value && (
                      <>
                        <Col sm={12} xs={24}>
                          <Form.Item
                            initialValue={
                              newOrganizationState.item.value.isTestingOrg
                                ? moment(newOrganizationState.item.value.testingEndDate)
                                : getDefaultOrganizationTestingEndDate()
                            }
                            name={nameof<FormData>('testingEndDate')}
                            rules={[() => ({ required: true })]}
                          >
                            <div style={{ width: '100' }}>
                              <Row>
                                <Col sm={12} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                                  <span style={{ float: 'inline-end' }}>{t('TestingEndDate')}</span>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item noStyle name={nameof<FormData>('testingEndDate')}>
                                    <DatePicker
                                      disabledDate={(current) => {
                                        let customDate = moment();
                                        if (newOrganizationState.item.value.isTestingOrg) {
                                          return (
                                            current &&
                                            (current < moment(customDate) || current > moment(newOrganizationState.item.value.testingEndDate))
                                          );
                                        }
                                        return current && current < moment(customDate);
                                      }}
                                      allowClear={false}
                                      style={{ width: '100%' }}
                                      format={getDateFormat()}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Row>
                </Tabs.TabPane>
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('OrganizationUsers')} key="2">
                  <Alert type="warning" message={t('OrganizationUserLimitWarningCreation').replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)} />
                  <Form.List name={nameof<FormData>('initialUsers')}>
                    {(fields, { add, remove }) => (
                      <>
                        <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                          {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                            <Row key={fieldKey}>
                              <Col span={11}>
                                <Form.Item
                                  {...restField}
                                  style={{ marginRight: 10 }}
                                  name={[fieldIndx, 'user']}
                                  rules={[
                                    () => ({
                                      required: true,
                                      validator(_, value) {
                                        if (value?.value === currentOrganizationMaster.current)
                                          return Promise.reject(t('ThisUserIsOrganizationMaster'));
                                        if (value?.value == null) return Promise.reject(t('PleaseSelectOrInvite'));
                                        if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <UserAutocomplete />
                                </Form.Item>
                              </Col>
                              <Col span={11}>
                                <Form.Item {...restField} style={{ marginRight: 10 }} name={[fieldIndx, 'roles']}>
                                  <DefaultRolesSelect
                                    excludeNames={[StructmasterUserRoleName]}
                                    placeholder={t('SelectRoles')}
                                    maxSelector={Selector.Organization}
                                    partnersOnly={false}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={2} style={{ textAlign: 'center' }}>
                                <MinusCircleOutlined style={{ fontSize: 20 }} onClick={() => remove(fieldIndx)} />
                              </Col>
                              <Col span={24}>
                                <Text style={{ marginBottom: 10 }} strong>
                                  {t('RoleAssignmentAttributes')}:
                                </Text>
                                <RoleAssignmentAttributesComponent
                                  customFormFieldName={[fieldIndx, 'roles-expiration']}
                                  onChange={(item) => onRoleAttributesChange(item, fieldIndx)}
                                />
                              </Col>
                            </Row>
                          ))}
                        </div>
                        <Form.Item>
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            disabled={fields.length >= ORGANIZATIONS_MEMBER_LIMIT}
                          >
                            {t('AddUser')}
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Tabs.TabPane>
              </Tabs>
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default NewOrganizationDialog;
